<template>
    <div class="w-full px-12 mt-48 xs:w-1/2 sm:w-1/3">
        <!--    CARD START-->
        <div>
            <div class="flex items-center">
                <div class="flex items-center justify-center flex-shrink-0 w-48 h-48 bg-yellow-200 rounded-full">
                    <div class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                        <IconNotesSVG class="w-24 h-24" />
                    </div>
                </div>

                <span class="ml-12 font-normal title-lg text-primary">{{ $t('info.my_info') }}</span>
            </div>

            <span v-if="customerInfo.firstname && customerInfo.lastname" class="mt-16 self-label">Nimi</span>
            <span v-if="customerInfo.firstname && customerInfo.lastname" class="text-base font-semibold sm:text-md"
                >{{ customerInfo.firstname }} {{ customerInfo.lastname }}</span
            >

            <span
                v-if="customerInfo.customertype == 'P'"
                class="mt-16 self-label"
                :class="!customerInfo.personal_code?.length && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                >{{ $t('info.identity_code') }}</span
            >
            <span v-if="customerInfo.customertype == 'P'" class="text-base font-semibold sm:text-md">{{
                customerInfo.personal_code?.length ? customerInfo.personal_code : '-'
            }}</span>

            <span
                v-if="customerInfo.customertype == 'P'"
                class="mt-16 self-label"
                :class="!birthDate?.length && customerInfo.customertype == 'P' ? 'text-alert' : ''"
                >{{ $t('info.dob') }}</span
            >
            <span v-if="customerInfo.customertype == 'P'" class="text-base font-semibold sm:text-md">{{
                birthDate?.length ? birthDate : '-'
            }}</span>

            <span v-if="customerInfo.customertype == 'P'" class="mt-16 self-label">{{ $t('info.gender') }}</span>
            <span v-if="customerInfo.customertype == 'P'" class="text-base font-semibold sm:text-md">{{
                $te('info.genders.' + customerInfo.gender) ? $t('info.genders.' + customerInfo.gender) : '-'
            }}</span>

            <button
                @click="
                    resetForm();
                    openModal('personal_data_modal');
                "
                class="mt-16 self-btn-plain"
            >
                <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                    <IconPencilSVG />
                </span>
                <span class="self-btn-plain__text">{{ $t('info.modify') }}</span>
            </button>
        </div>
        <!--    CARD END-->

        <!--     MODAL START-->
        <div class="micromodal" id="personal_data_modal" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">{{ $t('info.edit_my_info') }}</h2>

                        <button class="micromodal__close" aria-label="Close modal" data-micromodal-close :disabled="loading">
                            <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                                <IconXSVG />
                            </span>
                        </button>
                    </header>
                    <div class="micromodal__content">
                        <div class="flex self-form-wrap">
                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="name">{{ $t('info.name') }}</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="name"
                                        placeholder=""
                                        name="name"
                                        :value="customerInfo.firstname + ' ' + customerInfo.lastname"
                                        disabled
                                        class="e5-input"
                                    />
                                </div>
                            </div>

                            <div class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="personalCode">{{ $t('info.identity_code') }}</label>
                                <div class="relative">
                                    <input
                                        type="text"
                                        id="personalCode"
                                        placeholder=""
                                        name="personalCode"
                                        v-model="personalCode"
                                        :disabled="!!customerInfo.personal_code?.length || loading"
                                        class="e5-input"
                                    />
                                </div>
                            </div>

                            <Dob
                                v-if="!aboutToAddPersonalCode || customerInfo.personal_code?.length"
                                :label="$t('info.dob')"
                                :presetDate="birthDate"
                                @update:date="updateBirthDate"
                                is-dob
                                :disable="!!customerInfo.personal_code?.length || !!personalCode?.length || loading"
                                :uniqueId="`personal-data-${customerInfo.customerid}`"
                            />

                            <div v-if="!aboutToAddPersonalCode || customerInfo.personal_code?.length" class="w-full self-form-wrap-item">
                                <label class="e5-form-label" for="gender">{{ $t('info.gender') }}</label>

                                <div class="flex flex-col gap-2">
                                    <div class="block" v-for="gender in genders" :key="gender.value">
                                        <input
                                            type="radio"
                                            :id="gender.value"
                                            name="gender"
                                            :value="gender.value"
                                            class="e5-radio"
                                            v-model="selectedGender"
                                            :disabled="!!customerInfo.personal_code?.length || !!personalCode?.length || loading"
                                        />
                                        <label :for="gender.value">{{ $t(gender.labelKey) }}</label>
                                    </div>
                                </div>
                            </div>

                            <div class="flex items-center justify-between w-full px-8 pt-16">
                                <div>
                                    <button
                                        @click="submitChanges()"
                                        class="self-btn-primary"
                                        :disabled="loading || deleteOrEditPersonalCode || deleteOrEditBirthDate || deleteGender"
                                    >
                                        {{ !loading ? $t('info.save') : '' }}
                                        <span v-if="loading" class="grow-shrink-dot bg-yellow-300" role="status" aria-hidden="true"></span>
                                        <span v-else class="text-yellow-300 self-btn-primary__icon">
                                            <IconArrowAltSVG />
                                        </span>
                                    </button>
                                </div>
                                <div>
                                    <button data-micromodal-close class="self-btn-plain" :disabled="loading">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG />
                                        </span>
                                        <span class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--    MODAL END-->
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import IconNotesSVG from '@/assets/static/icons-src/icon-notes.svg';
import IconPencilSVG from '@/assets/static/icons-src/icon-pencil.svg';
import IconXSVG from '@/assets/static/icons-src/icon-x.svg';
import IconArrowAltSVG from '@/assets/static/icons-src/icon-arrow-alt.svg';
import { ICustomer } from '@/domain/ICustomer';
import Dob from '@/components/info-view-components/Dob.vue';
import { IBirthdateRequest } from '@/domain/ISaveInfo';
import store from '@/store';
import moment from 'moment';
import UtilsMixin from '@/scripts/utils';
import MicroModal from 'micromodal';

@Component({
    components: {
        IconNotesSVG,
        IconPencilSVG,
        IconXSVG,
        IconArrowAltSVG,
        Dob
    },
    mixins: [UtilsMixin]
})
export default class PersonalData extends Vue {
    birthDate = '';
    selectedGender = '';
    selectedBirthDate = '';
    personalCode = '';
    loading = false;
    genders = [
        { value: 'M', labelKey: 'info.genders.M' },
        { value: 'F', labelKey: 'info.genders.F' }
    ];

    get customerInfo(): ICustomer {
        return this.$store.getters.getCustomerInfo;
    }

    get deleteOrEditBirthDate(): boolean {
        return (
            (!this.selectedBirthDate?.length || !this.isSameDate(this.selectedBirthDate, this.customerInfo.birth_date)) &&
            this.customerInfo.birth_date?.length &&
            this.customerInfo?.customertype === 'P'
        );
    }

    get deleteGender(): boolean {
        return !this.selectedGender?.length && this.customerInfo.gender?.length && this.customerInfo?.customertype === 'P';
    }

    get deleteOrEditPersonalCode(): boolean {
        return (
            (!this.personalCode?.length || this.personalCode !== this.customerInfo.personal_code) &&
            this.customerInfo.personal_code?.length &&
            this.customerInfo?.customertype === 'P'
        );
    }

    created() {
        this.resetForm();
    }

    private calculateBirthAndGender() {
        if (this.birthDate?.length && this.selectedGender?.length) {
            return;
        }
        const personalCode = this.customerInfo.personal_code;
        if (personalCode?.length === 11) {
            const year = personalCode.substring(1, 3);
            const month = parseInt(personalCode.substring(3, 5));
            const day = parseInt(personalCode.substring(5, 7));

            // Determine the century based on the first digit of the personal code
            let century: string;
            switch (personalCode.charAt(0)) {
                case '1':
                case '2':
                    century = '18';
                    break;
                case '3':
                case '4':
                    century = '19';
                    break;
                case '5':
                case '6':
                    century = '20';
                    break;
                case '7':
                case '8':
                    century = '21';
                    break;
                default:
                    century = 'Invalid';
                    return;
            }

            // Construct the birthdate
            const birthDate = new Date(parseInt(century + year), month - 1, day);
            // Use customerInfo.birth_date if it exists, otherwise use the calculated birthdate
            this.birthDate = this.customerInfo.birth_date?.length
                ? moment(this.customerInfo.birth_date, 'YYYY-MM-DD').format('DD.MM.YYYY')
                : moment(birthDate).format('DD.MM.YYYY');

            // Use customerInfo.gender if it exists, otherwise determine gender based on the first digit of the personal code
            this.selectedGender = this.customerInfo.gender ?? (parseInt(personalCode.charAt(0)) % 2 === 0 ? 'F' : 'M');
        }
    }

    updateBirthDate(newBirthDate: string) {
        this.selectedBirthDate = newBirthDate;
    }

    get aboutToAddPersonalCode() {
        return this.personalCode?.length && !this.customerInfo.personal_code?.length;
    }

    isSameDate(date1: string, date2: string) {
        if (!date1?.length && !date2?.length) {
            return true;
        }
        if (date1?.length && date2?.length) {
            if (date1.includes('.')) {
                date1 = moment(date1.replace('.', '-'), 'DD-MM-YYYY').format('YYYY-MM-DD');
            }
            if (date2.includes('.')) {
                date2 = moment(date2.replace('.', '-'), 'DD-MM-YYYY').format('YYYY-MM-DD');
            }
            return moment(date1, 'YYYY-MM-DD').isSame(moment(date2, 'YYYY-MM-DD'));
        }
        return false;
    }

    async submitChanges() {
        this.loading = true;

        const results = {
            personalCode: { success: true, attempted: false },
            birthdate: { success: true, attempted: false },
            gender: { success: true, attempted: false }
        };
        // Save personal code
        if (this.aboutToAddPersonalCode) {
            results.personalCode.attempted = true;
            const request = {
                customerId: this.customerInfo.customerid,
                personalcode: this.personalCode
            };
            results.personalCode.success = await store.dispatch('addPersonalCode', request);
        }

        // Save birthdate
        // Only allowed if personal code is set and birthdate is different from customerInfo.birth_date
        if (
            !this.aboutToAddPersonalCode &&
            !this.customerInfo.personal_code?.length &&
            !this.isSameDate(this.selectedBirthDate, this.customerInfo.birth_date) &&
            this.selectedBirthDate?.length
        ) {
            results.birthdate.attempted = true;
            const request: IBirthdateRequest = {
                customerId: this.customerInfo.customerid,
                birthDate: this.selectedBirthDate.includes('.')
                    ? moment(this.selectedBirthDate.replace('.', '-'), 'DD-MM-YYYY').format('YYYY-MM-DD')
                    : this.selectedBirthDate
            };
            results.birthdate.success = await store.dispatch('saveBirthdate', request);
        }

        // Save gender
        if (!this.aboutToAddPersonalCode && this.selectedGender !== this.customerInfo.gender) {
            results.gender.attempted = true;
            const request = {
                customerId: this.customerInfo.customerid,
                gender: this.selectedGender
            };
            results.gender.success = await store.dispatch('saveGender', request);
        }

        // Check if any requests failed
        const failedRequests = Object.entries(results)
            .filter(([, result]) => result.attempted && !result.success)
            .map(([key]) => key);

        // Update customerinfo to display new personal data
        // Only update customer info if at least one request was attempted
        const anyRequestAttempted = Object.values(results).some((result) => result.attempted);
        MicroModal.close('personal_data_modal');
        if (anyRequestAttempted) {
            await store.dispatch('updateCustomerInfo');
        }

        if (failedRequests.length) {
            this.requestErrorFunction(failedRequests);
        }
        this.loading = false;
    }

    private requestErrorFunction(failedRequests: string[] = []) {
        const requestNames = {
            personalCode: this.$t('info.identity_code'),
            birthdate: this.$t('info.dob'),
            gender: this.$t('info.gender')
        };

        const failedRequestNames = failedRequests.map((req) => requestNames[req as keyof typeof requestNames] || req);
        const message =
            failedRequestNames.length === 1
                ? (this.$t('notifications.save_error_single', { field: failedRequestNames[0] }) as string)
                : (this.$t('notifications.save_error_multiple', { fields: failedRequestNames.join(', ') }) as string);

        Vue.notify({
            group: 'api',
            title: this.$t('notifications.server_error_title') as string,
            text: message,
            type: 'warn'
        });
    }

    resetForm() {
        this.personalCode = this.customerInfo.personal_code;
        if (this.customerInfo.birth_date?.length) {
            this.birthDate = moment(this.customerInfo.birth_date, 'YYYY-MM-DD').format('DD.MM.YYYY');
        }
        if (this.customerInfo.gender?.length) {
            this.selectedGender = this.customerInfo.gender;
        }
        this.calculateBirthAndGender();
        this.selectedBirthDate = this.birthDate;
    }
}
</script>

<style scoped></style>
